import * as _ from "lodash";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { WB_PeriodRow, WB_ProductConditionRow, WB_ProductConditionTypeRow } from "../PA.Core/PAdbSchemaRows";
import { esWB_Product_Condition } from "../PA.Core/PAdbSchema";
import * as AsyncLock from "async-lock";
import { Date_Default_Interval_Length, Product_Condition_Default_SetID } from "../PA.MD.Core/MD.Constants";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { PeriodService } from "../PA.MD.Services/PeriodService";
import { PeriodTypeService } from "../PA.MD.Services/PeriodTypeService";
import { ILease } from "./PA.UtilsIF";
import { LeaseRegistry } from "./LeaseRegistry";
import { GenericLease } from "./GenericLease";

export class ConditionContext {
    private readonly _dbContext: PAdbContext;
    private readonly conditionType: WB_ProductConditionTypeRow;
    private readonly customerWbID: number;
    private readonly productWbID: number;
    private readonly fundTypeWbID: number;
    private dctPrdCndRows: { [id: string]: WB_ProductConditionRow[] } = {};
    private bIsLoaded: boolean = false;
    private dctLock: any = new AsyncLock();

    private _lease: ILease;

    constructor(conditionType: WB_ProductConditionTypeRow, custWbID: number, prdWbID: number, fundType: number) {
        this._dbContext = new PAdbContext();
        this.conditionType = conditionType;
        this.customerWbID = custWbID;
        this.productWbID = prdWbID;
        this.fundTypeWbID = fundType;
        this.createLease();
    }

    findSingleCondition(
        custWbID: number,
        prdWbID: number,
        fundType: number,
        startDt: Date,
        endDt: Date,
    ): Promise<WB_ProductConditionRow> {
        const self = this;
        return new Promise<WB_ProductConditionRow>((resolve, error) => {
            let dctKey: string = ConditionContext.getConditionCacheKey(self.conditionType, custWbID, prdWbID, fundType);

            self.getConditionList(dctKey)
                .then((cndList) => {
                    resolve(ConditionContext.getFirstCondition(cndList, startDt, endDt));
                    return;
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    findConditionsUsingRange(
        custWbID: number,
        prdWbID: number,
        fundType: number,
        startDt: Date,
        endDt: Date,
    ): Promise<WB_ProductConditionRow[]> {
        const self = this;
        return new Promise<WB_ProductConditionRow[]>((resolve, error) => {
            let dctKey: string = ConditionContext.getConditionCacheKey(self.conditionType, custWbID, prdWbID, fundType);

            self.getConditionList(dctKey)
                .then((cndList) => {
                    resolve(ConditionContext.getConditionsInDateRange(cndList, startDt, endDt));
                    return;
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    findConditionsUsingInterval(
        custWbID: number,
        prdWbID: number,
        fundType: number,
        startDt: Date,
        intervalCount: number,
        intervalLengthInDays?: number,
    ): Promise<WB_ProductConditionRow[]> {
        const self = this;

        return new Promise<WB_ProductConditionRow[]>((resolve, error) => {
            let dctKey: string = ConditionContext.getConditionCacheKey(self.conditionType, custWbID, prdWbID, fundType);

            self.getConditionList(dctKey)
                .then((cndList) => {
                    if (_.isUndefined(intervalLengthInDays)) {
                        intervalLengthInDays = Date_Default_Interval_Length;
                    }

                    resolve(
                        ConditionContext.getConditionsInDateRange(
                            cndList,
                            startDt,
                            DateUtils.addDays(startDt, intervalCount * intervalLengthInDays),
                        ),
                    );
                    return;
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    getCndMapByPeriodWbIDUsingPeriodWbID(
        custWbID: number,
        prdWbID: number,
        fundType: number,
        parentPeriodWbID: number,
        periodTypeWbID: number,
    ): Promise<{ [periodWbID: number]: WB_ProductConditionRow }> {
        const self = this;
        return new Promise<{ [periodWbID: number]: WB_ProductConditionRow }>((resolve, error) => {
            let dctKey = ConditionContext.getConditionCacheKey(
                self.conditionType,
                custWbID,
                prdWbID, //if product policy, key by WB_Prod_ID
                fundType,
            );
            let retVal: { [periodID: string]: WB_ProductConditionRow } = {};
            let aCndRow: WB_ProductConditionRow;
            self.getConditionList(dctKey)
                .then((conditionRecords) => {
                    if (_.isArray(conditionRecords) && !_.isEmpty(conditionRecords)) {
                        PeriodService.getAllChildrenByWBIDChildTypeWbId(periodTypeWbID, parentPeriodWbID)
                            .then((periods) => {
                                _.forEach(periods, (aPeriod: WB_PeriodRow) => {
                                    //find condition that falls into period, then add it to the dictionary
                                    aCndRow = ConditionContext.getFirstCondition(
                                        conditionRecords,
                                        DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT),
                                        DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT),
                                    );

                                    if (!_.isUndefined(aCndRow)) {
                                        //create a copy so we don't modify the actual condition object in the cache
                                        let newCndRow = ConditionContext.createCopy(aCndRow);
                                        newCndRow.cif.EFFECTIVE_START_DT = aPeriod.cif.PERIOD_START_DT;
                                        newCndRow.cif.EFFECTIVE_END_DT = aPeriod.cif.PERIOD_END_DT;
                                        retVal[aPeriod.cif.WB_PERIOD_ID] = newCndRow;
                                    }
                                });
                                resolve(retVal);
                            })
                            .catch((err) => {
                                error(err);
                            });
                    } else {
                        resolve(retVal);
                    }
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    getCndMapByPeriodWbIDUsingDateRange(
        custWbID: number,
        prdWbID: number,
        fundType: number,
        startDt: Date,
        endDt: Date,
        periodTypeID: string,
    ): Promise<{ [periodWbID: number]: WB_ProductConditionRow }> {
        const self = this;
        return new Promise<{ [periodWbID: number]: WB_ProductConditionRow }>((resolve, error) => {
            let retVal: { [periodID: string]: WB_ProductConditionRow } = {};
            self.findConditionsUsingRange(custWbID, prdWbID, fundType, startDt, endDt)
                .then((conditionRecords) => {
                    if (_.isArray(conditionRecords) && !_.isEmpty(conditionRecords)) {
                        PeriodTypeService.getPeriodTypeByTypeID(periodTypeID)
                            .then((aPeriodType) => {
                                PeriodService.getPeriodsByPeriodTypeWbIDDateRange(
                                    aPeriodType.cif.WB_PERIODM_ID,
                                    endDt,
                                    startDt,
                                )
                                    .then((periods) => {
                                        let aCndRow: WB_ProductConditionRow;
                                        _.forEach(periods, (aPeriod) => {
                                            aCndRow = ConditionContext.getFirstCondition(
                                                conditionRecords,
                                                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT),
                                                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT),
                                            );

                                            if (!_.isUndefined(aCndRow)) {
                                                //create a copy so we don't modify the actual condition object in the cache
                                                let newCndRow = ConditionContext.createCopy(aCndRow);
                                                newCndRow.cif.EFFECTIVE_START_DT = aPeriod.cif.PERIOD_START_DT;
                                                newCndRow.cif.EFFECTIVE_END_DT = aPeriod.cif.PERIOD_END_DT;
                                                retVal[aPeriod.cif.WB_PERIOD_ID] = newCndRow;
                                            }
                                        });
                                        resolve(retVal);
                                    })
                                    .catch((err) => {
                                        error(err);
                                    });
                            })
                            .catch((err) => {
                                error(err);
                            });
                    } else {
                        resolve(retVal);
                    }
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    static getContextCacheKey(
        conditionType: WB_ProductConditionTypeRow,
        custWbID: number,
        prdWbID: number,
        fndTypeWbID: number,
    ): string {
        if (_.isEmpty(conditionType)) return;

        let keyParts: string[] = [conditionType.cif.PRD_CNDM_ID.toString()];

        switch (conditionType.cif.LEASE_SUBJECT) {
            case LeaseRegistry.Lease_Subject_Customer:
                if (custWbID > 0) keyParts.push(custWbID.toString());
                break;
            case LeaseRegistry.Lease_Subject_Product:
            case LeaseRegistry.Lease_Subject_ProductGroup:
                if (prdWbID > 0) keyParts.push(prdWbID.toString());
                break;
            case LeaseRegistry.Lease_Subject_FundType:
                if (fndTypeWbID > 0) keyParts.push(fndTypeWbID.toString());
                break;
        }

        return keyParts.join("-");
    }

    //<editor-fold desc="Private methods">

    private createLease() {
        this._lease = new GenericLease(LeaseRegistry.Lease_Type_ConditionContext);
        this._lease.addClasses(
            ConditionContext.getConditionLeaseClasses(
                this.conditionType,
                this.customerWbID,
                this.productWbID,
                this.fundTypeWbID,
            ),
        );
    }

    static getConditionLeaseClasses(
        conditionType: WB_ProductConditionTypeRow,
        custWbID: number,
        prdWbID: number,
        fndTypeWbID: number,
    ): string[] {
        if (_.isEmpty(conditionType)) return [];

        let retVal: string[] = [];

        switch (conditionType.cif.LEASE_SUBJECT) {
            case LeaseRegistry.Lease_Subject_Customer:
                retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Customer, custWbID));
                break;
            case LeaseRegistry.Lease_Subject_Product:
                retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Product, prdWbID));
                break;
            case LeaseRegistry.Lease_Subject_FundType:
                retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_FundType, fndTypeWbID));
                break;
            case LeaseRegistry.Lease_Subject_ProductGroup:
                retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_ProductGroup, prdWbID));
                break;
            case LeaseRegistry.Lease_Subject_ConditionType:
                retVal.push(
                    LeaseRegistry.getLeaseClass(
                        LeaseRegistry.Lease_Subject_ConditionType,
                        conditionType.cif.PRD_CNDM_ID,
                    ),
                );
                break;
        }

        return retVal;
    }

    private getConditionList(dctKey: string): Promise<WB_ProductConditionRow[]> {
        //get list of conditions for the given dictionary key, locking/loading as needed
        const self = this;

        return new Promise<WB_ProductConditionRow[]>((resolve, error) => {
            self.dctLock.acquire("global", (done: any) => {
                self.checkLease()
                    .then((ignore) => {
                        if (dctKey in self.dctPrdCndRows) {
                            done(null, self.dctPrdCndRows[dctKey]);
                            resolve(self.dctPrdCndRows[dctKey]);
                            return;
                        } else if (self.bIsLoaded && !(dctKey in self.dctPrdCndRows)) {
                            done(null, self.dctPrdCndRows[dctKey]);
                            resolve([]);
                            return;
                        }

                        self.loadConditionCache()
                            .then((ignore) => {
                                done(null, self.dctPrdCndRows[dctKey]);
                                resolve(self.dctPrdCndRows[dctKey]);
                                return;
                            })
                            .catch((err) => {
                                done(null, self.dctPrdCndRows[dctKey]);
                                error(err);
                            });
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    private async checkLease(): Promise<void> {
        if (await this._lease.hasDataChanged()) {
            await this.reset();
        }
    }

    private async reset() {
        //make array of conditions then call _dbContext.purge with that array of rows
        let cndArr: WB_ProductConditionRow[] = [];
        _.forEach(this.dctPrdCndRows, (aCnd: WB_ProductConditionRow[]) => {
            cndArr.push.apply(cndArr, aCnd);
        });

        await this._dbContext.purgeCache(esWB_Product_Condition, cndArr);
        this.dctPrdCndRows = {};
        this.bIsLoaded = false;
    }

    private loadConditionCache(): Promise<void> {
        const self = this;
        return new Promise<void>((resolve, error) => {
            if (_.isEmpty(self.conditionType)) return;
            let condQuery = self.createLoopbackQueryObject();
            self._dbContext
                .findAll(esWB_Product_Condition, condQuery)
                .then((results) => {
                    if (_.isArray(results)) {
                        let cnds = results as WB_ProductConditionRow[];
                        let dctKey: string = "";

                        _.forEach(cnds, (aCnd) => {
                            dctKey = ConditionContext.getConditionCacheKey(
                                self.conditionType,
                                aCnd.cif.WB_CUST_ID,
                                self.conditionType.cif.PRD_POLICY == "1" ? aCnd.cif.WB_PROD_ID : aCnd.cif.WB_PRDGRP_ID, //if product policy, key by WB_Prod_ID
                                aCnd.cif.WB_FUNDTYPE_ID,
                            );

                            if (_.isUndefined(self.dctPrdCndRows[dctKey])) {
                                self.dctPrdCndRows[dctKey] = [];
                            }

                            self.dctPrdCndRows[dctKey].push(aCnd);
                        });

                        self._lease.resetLease();
                        self.bIsLoaded = true;
                        resolve();
                    }
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    private createLoopbackQueryObject(): any {
        let conditions: any = {};
        let query: any = {};
        if (_.isEmpty(this.conditionType)) return;
        conditions.PROD_CNDM_ID = this.conditionType.cif.PRD_CNDM_ID;
        conditions.DELETED_IND = { neq: "1" };

        switch (this.conditionType.cif.LEASE_SUBJECT) {
            case LeaseRegistry.Lease_Subject_Customer:
                if (this.conditionType.cif.CUST_POLICY == "1") {
                    conditions.WB_CUST_ID = this.customerWbID;
                }
                break;
            case LeaseRegistry.Lease_Subject_Product:
                if (this.conditionType.cif.PRD_POLICY == "1") {
                    conditions.WB_PROD_ID = this.productWbID;
                }
                break;
            case LeaseRegistry.Lease_Subject_ProductGroup:
                if (this.conditionType.cif.PRDGRP_POLICY == "1") {
                    conditions.WB_PRDGRP_ID = this.productWbID;
                }
                break;
            case LeaseRegistry.Lease_Subject_FundType:
                if (this.conditionType.cif.FNDTYPE_POLICY == "1") {
                    conditions.WB_FUNDTYPE_ID = this.fundTypeWbID;
                }
                break;
        }

        query.where = conditions;
        query.order = "EFFECTIVE_START_DT asc";
        return query;
    }

    private static getConditionsInDateRange(
        cndRows: WB_ProductConditionRow[],
        startDt: Date,
        endDt: Date,
    ): WB_ProductConditionRow[] {
        return _.filter(cndRows, (aCndRow) => {
            return DateUtils.RangeDatesOverlap(
                startDt,
                endDt,
                DateUtils.convertFromSAPBW(aCndRow.cif.EFFECTIVE_START_DT),
                DateUtils.convertFromSAPBW(aCndRow.cif.EFFECTIVE_END_DT),
            );
        });
    }

    private static getFirstCondition(
        cndRows: WB_ProductConditionRow[],
        startDt: Date,
        endDt: Date,
    ): WB_ProductConditionRow {
        //assuming the cache is loaded, get the first condition that falls within the date range
        //also assumes array is sorted by startDt (should be done in loadConditionCache)
        return _.find(cndRows, (aCndRow: WB_ProductConditionRow) => {
            return DateUtils.RangeDatesOverlap(
                startDt,
                endDt,
                DateUtils.convertFromSAPBW(aCndRow.cif.EFFECTIVE_START_DT),
                DateUtils.convertFromSAPBW(aCndRow.cif.EFFECTIVE_END_DT),
            );
        });
    }

    private static getConditionCacheKey(
        conditionType: WB_ProductConditionTypeRow,
        custWbID: number,
        prdWbID: number,
        fndTypeWbID: number,
    ): string {
        if (_.isEmpty(conditionType)) return "";

        let keyParts: string[] = [];
        if (conditionType.cif.CUST_POLICY == "1" && custWbID > 0) {
            keyParts.push(custWbID.toString());
        }

        if ((conditionType.cif.PRD_POLICY == "1" || conditionType.cif.PRDGRP_POLICY == "1") && prdWbID > 0) {
            keyParts.push(prdWbID.toString());
        }

        if (conditionType.cif.FNDTYPE_POLICY == "1" && fndTypeWbID > 0) {
            keyParts.push(fndTypeWbID.toString());
        }

        return keyParts.join("-");
    }

    private static createCopy(original: WB_ProductConditionRow): WB_ProductConditionRow {
        return new WB_ProductConditionRow(
            {
                PROD_CND_ID: original.cif.PROD_CND_ID,
                SOURCE_ID: original.cif.SOURCE_ID,
                PROD_CNDM_ID: original.cif.PROD_CNDM_ID,
                REL_SOURCE_ID: original.cif.REL_SOURCE_ID,
                CONDITION_TYPE: original.cif.CONDITION_TYPE,
                PRODUCT_ID: original.cif.PRODUCT_ID,
                CUSTOMER_ID: original.cif.CUSTOMER_ID,
                VALUE_RATEAMOUNT: original.cif.VALUE_RATEAMOUNT,
                VALUE_UOM: original.cif.VALUE_UOM,
                VALUE_BASISQTY: original.cif.VALUE_BASISQTY,
                EFFECTIVE_START_DT: original.cif.EFFECTIVE_START_DT,
                EFFECTIVE_END_DT: original.cif.EFFECTIVE_END_DT,
                CURRENCY_ID: original.cif.CURRENCY_ID,
                SALESORG: original.cif.SALESORG,
                DIVISION: original.cif.DIVISION,
                DISTRIBCHANNEL: original.cif.DISTRIBCHANNEL,
                INTEGRATION_IND: original.cif.INTEGRATION_IND,
                DELETED_IND: original.cif.DELETED_IND,
                DELETED_DT: original.cif.DELETED_DT,
                WB_CUST_ID: original.cif.WB_CUST_ID,
                WB_PROD_ID: original.cif.WB_PROD_ID,
                WB_PRDGRP_ID: original.cif.WB_PRDGRP_ID,
                WB_FUNDTYPE_ID: original.cif.WB_FUNDTYPE_ID,
            },
            {},
        );
    }

    //</editor-fold>
}
